import type {
    VisitorIqFilterSettingsFragment as VisitorIqFilterSettings,
    VisitorIqFilterSettingsUpdateInput as SelectedVisitorIqFilterSettings,
    PeopleIqFilterSettingsFragment as PeopleIqFilterSettings,
} from 'nutshell-graphql-types';

type SavedFilterSettings = PeopleIqFilterSettings | VisitorIqFilterSettings;
type FilterSettingsToSave = SelectedVisitorIqFilterSettings | PeopleIqFilterSettings;

// industries | contactInformation | jobField | managementLevel
export type SettingsGroupKey = keyof SelectedVisitorIqFilterSettings;

export const getSelectedFilterSettings = (
    savedFilterSettings: SavedFilterSettings,
    includeIndustries = true
): FilterSettingsToSave => {
    // @ts-expect-error
    const mcfxIndustries = savedFilterSettings.mcfxIndustries
        ? // @ts-expect-error
          savedFilterSettings.mcfxIndustries
        : [];
    const {contactInformationList, jobFieldList, managementLevelList} = savedFilterSettings;
    const nonIndustriesSaveSettings = {
        contactInformation: contactInformationList
            .filter(({isBlocked}) => !isBlocked)
            .map(({contactInformation}) => contactInformation.id)
            .sort(),
        jobField: jobFieldList
            .filter(({isBlocked}) => !isBlocked)
            .map(({jobField}) => jobField.id)
            .sort(),
        managementLevel: managementLevelList
            .filter(({isBlocked}) => !isBlocked)
            .map(({managementLevel}) => managementLevel.id)
            .sort(),
    } as const;

    if (!includeIndustries) {
        // @ts-expect-error
        return nonIndustriesSaveSettings;
    }

    const urlBlocklist =
        'urlBlocklist' in savedFilterSettings && Array.isArray(savedFilterSettings.urlBlocklist)
            ? savedFilterSettings.urlBlocklist
            : [];

    return {
        industries: mcfxIndustries
            // @ts-expect-error
            .filter((mcfxIndustry) => mcfxIndustry && !mcfxIndustry.isBlocked)
            // @ts-expect-error
            .map(({industry}) => industry.id)
            .sort(),
        ...nonIndustriesSaveSettings,
        urlBlocklist,
    } as FilterSettingsToSave;
};

export const getAllSettingsFromFilterSettings = (
    savedFilterSettings: SavedFilterSettings,
    includeIndustries = true
): Partial<
    Record<
        SettingsGroupKey,
        {
            id: string;
            name: string;
        }[]
    >
> => {
    // @ts-expect-error
    const mcfxIndustries = savedFilterSettings.mcfxIndustries
        ? // @ts-expect-error
          savedFilterSettings.mcfxIndustries
        : [];
    const {contactInformationList, jobFieldList, managementLevelList} = savedFilterSettings;

    const nonIndustriesSettings = {
        ...{
            managementLevel: managementLevelList.map(({managementLevel}) => ({
                id: managementLevel.id,
                name: managementLevel.name,
            })),
            jobField: jobFieldList.map(({jobField}) => ({
                id: jobField.id,
                name: jobField.name,
            })),
            contactInformation: contactInformationList.map(({contactInformation}) => ({
                id: contactInformation.id,
                name: contactInformation.name,
            })),
        },
    } as const;

    return includeIndustries
        ? {
              industries: mcfxIndustries
                  // @ts-expect-error
                  .filter((industry) => industry)
                  // @ts-expect-error
                  .map(({industry}) => ({
                      id: industry.id,
                      name: industry.name,
                  })),
              ...nonIndustriesSettings,
          }
        : nonIndustriesSettings;
};

export const getHeaderAndSubheaderBySettingsGroupKey = (
    settingKey: SettingsGroupKey
): {
    header: string;
    subheader: string;
} => {
    switch (settingKey) {
        case 'industries':
            return {
                header: 'Industries',
                subheader: 'Only track companies that are part of these business sectors:',
            };
        case 'managementLevel':
            return {
                header: 'Management level',
                subheader: 'Only show me people at these management levels:',
            };
        case 'jobField':
            return {
                header: 'Job field',
                subheader: 'Only show me people in these roles & professions:',
            };
        case 'contactInformation':
            return {
                header: 'Contact information',
                subheader: 'Only show me people with these contact details:',
            };
        default:
            // Case should never be hit, but needs to return something for Flow
            return {header: '', subheader: ''};
    }
};

export const handleSettingSelectionChange = (
    setSelectedSettings: (arg1?: any) => void,
    settingsGroupKey: SettingsGroupKey,
    settingId: string,
    checked: boolean
) => {
    // @ts-expect-error
    setSelectedSettings((prevSelectedSettings) => {
        // get previous selection for the given group
        const prevCheckedValuesForGroup: any[] = prevSelectedSettings
            ? prevSelectedSettings[settingsGroupKey]
            : [];

        // update the selection for the given group
        const newCheckedValuesForGroup = checked
            ? [...prevCheckedValuesForGroup, settingId]
            : prevCheckedValuesForGroup.filter((id) => id !== settingId);

        return {
            ...prevSelectedSettings,
            [settingsGroupKey]: newCheckedValuesForGroup,
        };
    });
};
